/**
 * Resizable Panels Component
 * Three-panel layout with draggable dividers for resizing
 */

import { Box } from '@mui/material';
import { useState, useRef, useEffect, ReactNode } from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';

interface ResizablePanelsProps {
  leftPanel: ReactNode;
  centerPanel: ReactNode;
  rightPanel: ReactNode;
  height?: string;
  defaultLeftWidth?: number;
  defaultRightWidth?: number;
  minPanelWidth?: number;
}

export default function ResizablePanels({
  leftPanel,
  centerPanel,
  rightPanel,
  height = '700px',
  defaultLeftWidth = 400,
  defaultRightWidth = 350,
  minPanelWidth = 250,
}: ResizablePanelsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftWidth, setLeftWidth] = useLocalStorage('resizablePanels.leftWidth', defaultLeftWidth);
  const [rightWidth, setRightWidth] = useLocalStorage('resizablePanels.rightWidth', defaultRightWidth);
  const [isDraggingLeft, setIsDraggingLeft] = useState(false);
  const [isDraggingRight, setIsDraggingRight] = useState(false);

  // Handle left divider drag
  const handleLeftMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingLeft(true);
  };

  // Handle right divider drag
  const handleRightMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingRight(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const containerWidth = containerRect.width;
      const mouseX = e.clientX - containerRect.left;

      if (isDraggingLeft) {
        const newLeftWidth = Math.max(minPanelWidth, Math.min(mouseX, containerWidth - rightWidth - minPanelWidth - 16)); // 16px for gaps
        setLeftWidth(newLeftWidth);
      }

      if (isDraggingRight) {
        const newRightWidth = Math.max(minPanelWidth, Math.min(containerWidth - mouseX, containerWidth - leftWidth - minPanelWidth - 16));
        setRightWidth(newRightWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDraggingLeft(false);
      setIsDraggingRight(false);
      // Trigger window resize event to notify Plotly and other responsive components
      window.dispatchEvent(new Event('resize'));
    };

    if (isDraggingLeft || isDraggingRight) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDraggingLeft, isDraggingRight, leftWidth, rightWidth, minPanelWidth, setLeftWidth, setRightWidth]);

  const centerWidth = `calc(100% - ${leftWidth}px - ${rightWidth}px - 16px)`; // 16px for 2 gaps

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        height,
        gap: 0,
        position: 'relative',
      }}
    >
      {/* Left Panel */}
      <Box
        sx={{
          width: `${leftWidth}px`,
          flexShrink: 0,
          overflow: 'auto',
        }}
      >
        {leftPanel}
      </Box>

      {/* Left Divider */}
      <Box
        onMouseDown={handleLeftMouseDown}
        sx={{
          width: '8px',
          flexShrink: 0,
          cursor: 'col-resize',
          backgroundColor: 'transparent',
          position: 'relative',
          '&:hover': {
            backgroundColor: 'primary.main',
            opacity: 0.3,
          },
          '&:active': {
            backgroundColor: 'primary.main',
            opacity: 0.5,
          },
        }}
      />

      {/* Center Panel */}
      <Box
        sx={{
          width: centerWidth,
          flexGrow: 1,
          flexShrink: 1,
          overflow: 'hidden',
        }}
      >
        {centerPanel}
      </Box>

      {/* Right Divider */}
      <Box
        onMouseDown={handleRightMouseDown}
        sx={{
          width: '8px',
          flexShrink: 0,
          cursor: 'col-resize',
          backgroundColor: 'transparent',
          position: 'relative',
          '&:hover': {
            backgroundColor: 'primary.main',
            opacity: 0.3,
          },
          '&:active': {
            backgroundColor: 'primary.main',
            opacity: 0.5,
          },
        }}
      />

      {/* Right Panel */}
      <Box
        sx={{
          width: `${rightWidth}px`,
          flexShrink: 0,
          overflow: 'auto',
        }}
      >
        {rightPanel}
      </Box>
    </Box>
  );
}
